<script setup lang="ts">
import {
  DataPointDetailsDto,
  EntityWithCommentType,
  instanceOfEnumerationDataPointDetailsDto,
  instanceOfNumericDataPointDetailsDto,
} from '@/api/generator';
import { api } from '@/api';
import Comments from '@/components/comments/Comments.vue';
import { watchEffect, ref, computed } from 'vue';
import Dialog from '@/components/Dialog/Dialog.vue';
import { injectStrict } from '@/utils/injectStrict';
import { getFormattingOptionsFromEvaluationParameter, toDisplayValue } from '@/utils/numberUtils';
import { reloadTrendsKey } from './types';
import { getFullEvaluationTypeName } from '@/views/TrendsDisplayView/utils';
import EvaluationResultHistory from '../results/EvaluationResultHistory.vue';
import EvaluationState from '../evaluation/EvaluationState.vue';
import LinearGauge from '../charts/LinearGauge.vue';
import { getLimitedValue } from '../charts/utils/limitedValue';

export interface DatapointDialogProps {
  datapointId: string;
}

const props = defineProps<DatapointDialogProps>();
const dataPointDetails = ref<DataPointDetailsDto | null>(null);

const dataPointDetailsNumeric = computed(() =>
  dataPointDetails.value && instanceOfNumericDataPointDetailsDto(dataPointDetails.value)
    ? dataPointDetails.value
    : null,
);

const dataPointDetailsEnumeration = computed(() =>
  dataPointDetails.value && instanceOfEnumerationDataPointDetailsDto(dataPointDetails.value)
    ? dataPointDetails.value
    : null,
);

const reloadTrends = injectStrict(reloadTrendsKey);

watchEffect(async () => {
  if (props.datapointId) {
    dataPointDetails.value = null;
    const { result } = await api.dataSetApi.getDataPointDetails({
      dataPointId: props.datapointId,
    });
    if (result) {
      dataPointDetails.value = result;
    }
  }
});

const emits = defineEmits<{
  'update:datapointId': [datapointId: string];
}>();

function close(open: boolean = false) {
  if (!open) {
    emits('update:datapointId', '');
  }
}

const formattingOptions = computed(() =>
  dataPointDetailsNumeric.value?.evaluationParameter
    ? getFormattingOptionsFromEvaluationParameter(dataPointDetailsNumeric.value.evaluationParameter)
    : undefined,
);

const displayValue = computed(() => {
  if (dataPointDetailsEnumeration.value) {
    return dataPointDetailsEnumeration.value.evaluationResult.selectedOption?.name;
  } else if (dataPointDetailsNumeric.value) {
    return toDisplayValue(
      dataPointDetailsNumeric.value.evaluationResult.deviationValue,
      formattingOptions.value,
    );
  } else {
    throw new Error('should not happen');
  }
});

const headline = computed(() =>
  getFullEvaluationTypeName(dataPointDetails.value?.evaluationParameter),
);

function onCommentsChange() {
  reloadTrends();
}

const gaugeData = computed(() =>
  dataPointDetailsNumeric.value
    ? {
        value: getLimitedValue(
          dataPointDetailsNumeric.value.evaluationResult,
          dataPointDetailsNumeric.value.evaluationParameter,
        ),
        unit: dataPointDetailsNumeric.value.evaluationResult.deviationUnit,
      }
    : undefined,
);
</script>

<template>
  <Dialog :modelValue="Boolean(datapointId)" size="large" @update:modelValue="close">
    <template #title>{{ headline }}</template>

    <template v-if="dataPointDetails" #text>
      <div class="d-flex">
        <div class="mr-4">
          <v-label class="d-block">
            {{ $t('Value') }}
          </v-label>
          {{ displayValue }}
        </div>

        <LinearGauge
          v-if="gaugeData?.value"
          :value="gaugeData.value"
          :unit="gaugeData.unit"
          class="flex-fill"
          :style="{ alignSelf: 'center' }"
        />
        <div class="ml-4">
          <v-label class="d-block"> {{ $t('Result_one') }}</v-label>
          <EvaluationState :value="dataPointDetails.evaluationResult.evaluationOutcome" />
        </div>
      </div>

      <EvaluationResultHistory class="mt-4" :value="dataPointDetails" />

      <Comments
        v-if="dataPointDetails"
        :id="dataPointDetails.evaluationResult.id"
        class="mt-4"
        :entityWithCommentType="EntityWithCommentType.EvaluationResultOrDataPoint"
        @change="onCommentsChange"
      />
    </template>

    <template #actions>
      <PrimaryVBtn :text="$t('Close')" @click="close(false)" />
    </template>
  </Dialog>
</template>
